import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { Mail, CheckCircle2, XCircle, RefreshCw, Search } from "lucide-react";

const DS = {
    indigo: "#4F46E5", indigoLight: "#EEF2FF",
    emerald: "#059669", emeraldLight: "#ECFDF5",
    rose: "#E11D48", roseLight: "#FFF1F2",
    bg: "#F8FAFC", surface: "#FFFFFF", surfaceAlt: "#F8FAFC",
    border: "#E2E8F0", borderSolid: "#E8E0F5", dark: "#0F172A",
    gray: "#64748B", faint: "#94A3B8", violetFaint: "#F7F3FD",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function MailLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [roleFilter, setRoleFilter] = useState("All");

    const fetchLogs = async () => {
        try {
            const res = await API.get("/mail-logs");
            setLogs(res.data.data || []);
        } catch (err) {
            console.error("Mail logs fetch error:", err);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchLogs().finally(() => setLoading(false));
    }, []);

    const getCategory = (log) => {
        const role = (log.recipient_role || "").toLowerCase();
        const isAdmin = Number(log.recipient_is_admin) === 1 || role === "admin";
        if (isAdmin) return "admin";
        if (role === "client") return "client";
        if (role === "engineer") return "engineer";
        if (role === "manager") return "manager";
        return "other";
    };

    const getRoleBadgeStyle = (category) => {
        switch (category) {
            case "client":
                return { color: "#0284C7", bg: "#E0F2FE", label: "Client" };
            case "engineer":
                return { color: "#D97706", bg: "#FEF3C7", label: "Engineer" };
            case "manager":
                return { color: "#7C3AED", bg: "#F5F3FF", label: "Manager" };
            case "admin":
                return { color: "#E11D48", bg: "#FFF1F2", label: "Admin" };
            default:
                return { color: "#64748B", bg: "#F1F5F9", label: "User" };
        }
    };

    const clientCount = logs.filter(l => getCategory(l) === "client").length;
    const engineerCount = logs.filter(l => getCategory(l) === "engineer").length;
    const managerCount = logs.filter(l => getCategory(l) === "manager").length;
    const adminCount = logs.filter(l => getCategory(l) === "admin").length;
    const successCount = logs.filter(l => l.status === "success").length;
    const failedCount = logs.filter(l => l.status === "failed").length;

    const filtered = logs.filter((l) => {
        const q = search.toLowerCase().trim();
        const cat = getCategory(l);
        const badge = getRoleBadgeStyle(cat);
        const isInternal = cat === "manager" || cat === "admin";

        const matchSearch = !q
            || (l.recipient || "").toLowerCase().includes(q)
            || (l.subject || "").toLowerCase().includes(q)
            || (l.recipient_name || "").toLowerCase().includes(q)
            || (badge.label || "").toLowerCase().includes(q)
            || (isInternal && "internal".includes(q))
            || (l.error_message || "").toLowerCase().includes(q);

        const matchStatus = statusFilter === "All" || l.status === statusFilter.toLowerCase();
        const matchRole = roleFilter === "All"
            || (roleFilter.toLowerCase() === "internal" ? isInternal : cat === roleFilter.toLowerCase());

        return matchSearch && matchStatus && matchRole;
    });

    return (
        <AdminLayout>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap'); * { font-family: 'Montserrat', sans-serif !important; }`}</style>
            <div style={{ padding: "28px 32px", background: DS.bg, minHeight: "100vh" }}>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 14 }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${DS.indigo},${DS.rose})` }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: DS.faint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                                Admin · Masters
                            </span>
                        </div>
                        <h1 style={{ fontWeight: 800, fontSize: 28, color: DS.dark, margin: 0 }}>Mail Logs</h1>
                        <p style={{ color: DS.faint, fontSize: 13, margin: "4px 0 0" }}>Track every email sent by the system — successes and failures</p>
                    </div>
                    <button
                        onClick={async () => { setRefreshing(true); await fetchLogs(); setRefreshing(false); }}
                        disabled={refreshing}
                        style={{
                            width: 40, height: 40, borderRadius: 11, border: `1.5px solid ${DS.borderSolid}`,
                            background: DS.surface, color: DS.gray, display: "flex", alignItems: "center",
                            justifyContent: "center", cursor: refreshing ? "not-allowed" : "pointer",
                        }}
                        title="Refresh"
                    >
                        <RefreshCw size={16} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none" }} />
                    </button>
                </div>

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

                {/* KPI cards with Role & Count summary */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 24 }}>
                    {[
                        { id: "All", label: "Total Emails", count: logs.length, sub: `${successCount} sent · ${failedCount} failed`, color: DS.indigo },
                        { id: "Client", label: "Client Mails", count: clientCount, sub: "Registered stakeholders", color: "#0284C7" },
                        { id: "Engineer", label: "Engineer Mails", count: engineerCount, sub: "Field engineers", color: "#D97706" },
                        { id: "Manager", label: "Manager Mails", count: managerCount, sub: "Project managers", color: "#7C3AED" },
                        { id: "Admin", label: "Admin Mails", count: adminCount, sub: "Super administrators", color: "#E11D48" },
                    ].map((card) => {
                        const isSelected = roleFilter === card.id;
                        return (
                            <div
                                key={card.id}
                                onClick={() => setRoleFilter(card.id)}
                                style={{
                                    background: DS.surface,
                                    borderRadius: 16,
                                    border: `1.5px solid ${isSelected ? card.color : DS.borderSolid}`,
                                    padding: "18px 20px",
                                    cursor: "pointer",
                                    boxShadow: isSelected ? `0 4px 16px ${card.color}22` : "none",
                                    transition: "all 0.15s ease",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: isSelected ? card.color : DS.faint, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                        {card.label}
                                    </span>
                                    <span style={{
                                        width: 8, height: 8, borderRadius: "50%",
                                        background: isSelected ? card.color : "transparent",
                                    }} />
                                </div>
                                <div style={{ fontWeight: 800, fontSize: 26, color: card.color, marginBottom: 4 }}>
                                    {card.count}
                                </div>
                                <div style={{ fontSize: 11, color: DS.faint, fontWeight: 500 }}>
                                    {card.sub}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Table card */}
                <div style={{ background: DS.surface, borderRadius: 20, border: `1px solid ${DS.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(79,70,229,0.08)" }}>
                    <div style={{ height: 4, background: `linear-gradient(90deg,${DS.indigo},${DS.rose})` }} />
                    <div style={{ padding: "20px 24px" }}>
                        {/* Filters Bar */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                                {/* Recipient Role Filter Tabs */}
                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>
                                        Role:
                                    </span>
                                    {[
                                        { id: "All", label: "All", count: logs.length },
                                        { id: "Client", label: "Client", count: clientCount, activeColor: "#0284C7" },
                                        { id: "Engineer", label: "Engineer", count: engineerCount, activeColor: "#D97706" },
                                        { id: "Manager", label: "Manager", count: managerCount, activeColor: "#7C3AED" },
                                        { id: "Admin", label: "Admin", count: adminCount, activeColor: "#E11D48" },
                                    ].map((tab) => {
                                        const isActive = roleFilter === tab.id;
                                        const activeColor = tab.activeColor || DS.indigo;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setRoleFilter(tab.id)}
                                                style={{
                                                    fontSize: 12,
                                                    padding: "6px 14px",
                                                    borderRadius: 20,
                                                    fontWeight: 600,
                                                    border: `1.5px solid ${isActive ? activeColor : DS.borderSolid}`,
                                                    background: isActive ? activeColor : DS.surface,
                                                    color: isActive ? "#ffffff" : DS.gray,
                                                    cursor: "pointer",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    transition: "all 0.15s ease",
                                                }}
                                            >
                                                <span>{tab.label}</span>
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    padding: "1px 7px",
                                                    borderRadius: 10,
                                                    background: isActive ? "rgba(255,255,255,0.25)" : DS.violetFaint,
                                                    color: isActive ? "#ffffff" : DS.dark,
                                                }}>
                                                    {tab.count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Search Bar */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8, background: DS.surfaceAlt, border: `1.5px solid ${DS.borderSolid}`, borderRadius: 12, padding: "9px 14px", minWidth: 260, flex: "1 1 260px", maxWidth: 360 }}>
                                    <Search size={15} color={DS.faint} />
                                    <input
                                        type="text"
                                        placeholder="Search recipient, name, subject…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        style={{ border: "none", background: "transparent", fontSize: 13, color: DS.dark, outline: "none", width: "100%" }}
                                    />
                                </div>
                            </div>

                            {/* Secondary Status Filter row */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingTop: 8, borderTop: `1px dashed ${DS.borderSolid}` }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>
                                    Status:
                                </span>
                                {[
                                    { id: "All", label: "All", count: logs.length },
                                    { id: "Success", label: "Success", count: successCount },
                                    { id: "Failed", label: "Failed", count: failedCount },
                                ].map((s) => {
                                    const isActive = statusFilter === s.id;
                                    const activeColor = s.id === "Success" ? DS.emerald : s.id === "Failed" ? DS.rose : DS.indigo;
                                    return (
                                        <button
                                            key={s.id}
                                            onClick={() => setStatusFilter(s.id)}
                                            style={{
                                                fontSize: 11,
                                                padding: "4px 12px",
                                                borderRadius: 14,
                                                fontWeight: 600,
                                                border: `1px solid ${isActive ? activeColor : DS.borderSolid}`,
                                                background: isActive ? (s.id === "Success" ? DS.emeraldLight : s.id === "Failed" ? DS.roseLight : DS.indigoLight) : DS.surface,
                                                color: isActive ? activeColor : DS.gray,
                                                cursor: "pointer",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 5,
                                            }}
                                        >
                                            <span>{s.label}</span>
                                            <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.85 }}>({s.count})</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {loading ? (
                            <div style={{ textAlign: "center", padding: "50px 0", color: DS.faint, fontSize: 13 }}>Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "50px 0", color: DS.faint }}>
                                <Mail size={36} style={{ marginBottom: 10 }} />
                                <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark }}>No mail logs found</div>
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 900 }}>
                                    <thead>
                                        <tr style={{ background: DS.violetFaint }}>
                                            {["#", "Recipient", "Subject", "Status", "Error", "Date"].map((h, i) => (
                                                <th key={h} style={{
                                                    fontSize: 10, fontWeight: 700, color: DS.gray, textTransform: "uppercase",
                                                    letterSpacing: "0.08em", padding: "12px 16px", textAlign: i === 0 ? "center" : "left",
                                                    borderBottom: `2px solid ${DS.borderSolid}`, whiteSpace: "nowrap",
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((log, i) => {
                                            const roleBadge = getRoleBadgeStyle(getCategory(log));
                                            return (
                                                <tr key={log.id} style={{ borderBottom: `1px solid ${DS.borderSolid}` }}>
                                                    <td style={{ padding: "13px 16px", textAlign: "center" }}>
                                                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 7, background: DS.violetFaint, fontWeight: 700, fontSize: 11, color: DS.faint }}>{i + 1}</span>
                                                    </td>
                                                    <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <span style={{ fontSize: 13, fontWeight: 600, color: DS.dark }}>
                                                                {log.recipient || "—"}
                                                            </span>
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                padding: "2px 8px",
                                                                borderRadius: 12,
                                                                color: roleBadge.color,
                                                                background: roleBadge.bg,
                                                                textTransform: "uppercase",
                                                                letterSpacing: "0.04em",
                                                            }}>
                                                                {roleBadge.label}
                                                            </span>
                                                        </div>
                                                        {log.recipient_name && (
                                                            <div style={{ fontSize: 11, color: DS.faint, marginTop: 2, fontWeight: 500 }}>
                                                                {log.recipient_name}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: "13px 16px", fontSize: 12, color: DS.gray, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.subject || "—"}</td>
                                                    <td style={{ padding: "13px 16px" }}>
                                                        {log.status === "success" ? (
                                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: DS.emerald, background: DS.emeraldLight, padding: "5px 11px", borderRadius: 20 }}>
                                                                <CheckCircle2 size={12} /> Success
                                                            </span>
                                                        ) : (
                                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: DS.rose, background: DS.roseLight, padding: "5px 11px", borderRadius: 20 }}>
                                                                <XCircle size={12} /> Failed
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: "13px 16px", fontSize: 12, color: DS.rose, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.error_message || "—"}</td>
                                                    <td style={{ padding: "13px 16px", fontSize: 12, color: DS.gray, whiteSpace: "nowrap" }}>{formatDate(log.created_at)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}